import { BaseModel } from "../../../service/models/base";
import { MODULE } from "../../../utils/enums";
import { AdditionalFund, ApprovalModel } from "./cost_center_model";

export interface Project extends BaseModel {
  name: string;
  description?: string;
  cost_center_category: string;
  cost_center_id: string;
  batchName: string;
  batchSize: number;
  budget: number;
  spent: number;
  addAdditionalBudget?: number;
  totalAdditionalFunds: number;
  additionalFunds?: AdditionalFund[];
  totalBudget: number;
  projectStatus: string;
  lastUpdatedBudget: number;
  // --- Phase 3 new-flow fields (mirror of backend project_model.ts)
  business_unit_id?: string;
  business_unit_name?: string;
  business_unit_code?: string;
  department_id?: string;
  department_name?: string;
  department_code?: string;
  original_budget?: number;
  approval_level?: string;
  approvals?: ApprovalModel[];
  current_approval_level?: string;
  current_approval_required_count?: number;
  current_approval_count?: number;
  current_reject_count?: number;
  approvalStatus?: string;
  approved_at?: Date;
  approved_by?: string;
  approved_by_name?: string;
  rejectReason?: string;
  remarks?: string;
}

export const ProjectCloseStatus = {
  closed: "Closed",
  open: "Open",
};

export const projectDefaultValue: Project = {
  name: "",
  batchName: "",
  batchSize: 0,
  budget: 0,
  totalBudget: 0,
  cost_center_id: "",
  spent: 0,
  projectStatus: ProjectCloseStatus.open,
  totalAdditionalFunds: 0,
  cost_center_category: "",
  lastUpdatedBudget: 0,
};

export interface ProjectFilterData {
  name?: string[];
  cost_center?: string;
  cost_center_id?: string[];
  description?: string;
  projectStatus?: string[];
  from?: Date;
  to?: Date;
  search?: string;
  page: number;
  count: number;
  all?: any;
  id?: string;
}

// =============================================================================
// Phase 3: project approval constants — mirror of backend project_model.ts.
// =============================================================================

export const ProjectApprovalStatus = {
  pendingForApproval: "Pending for Approval",
  pendingDeptHead: "Pending Department Head Approval",
  pendingFinance: "Pending Finance Approval",
  approved: "Approved",
  rejected: "Rejected",
};

export const project_approval_level = {
  bu_head: "bu_head",
  dept_head: "dept_head",
  finance_L1: "finance_L1",
  finance_L2: "finance_L2",
  finance_L3: "finance_L3",
  bu_dept_finance_L1: "bu_dept_finance_L1",
  bu_dept_finance_L2: "bu_dept_finance_L2",
  bu_dept_finance_L3: "bu_dept_finance_L3",
};

export const project_levelModule: { [k: string]: string } = {
  [project_approval_level.bu_head]: MODULE.business_unit_head,
  [project_approval_level.dept_head]: MODULE.department_head,
  [project_approval_level.finance_L1]: MODULE.finance_approval_L1,
  [project_approval_level.finance_L2]: MODULE.finance_approval_L2,
  [project_approval_level.finance_L3]: MODULE.finance_approval_L3,
};

export const project_approval_threshold: {
  [k: string]: {
    threshold: { min: number | null; max: number | null };
    approvals_required: { level: string; count: number }[];
  };
} = {
  [project_approval_level.bu_dept_finance_L1]: {
    threshold: { min: null, max: null },
    approvals_required: [
      { level: project_approval_level.bu_head, count: 1 },
      { level: project_approval_level.dept_head, count: 1 },
      { level: project_approval_level.finance_L1, count: 1 },
    ],
  },
  [project_approval_level.bu_dept_finance_L2]: {
    threshold: { min: null, max: null },
    approvals_required: [
      { level: project_approval_level.bu_head, count: 1 },
      { level: project_approval_level.dept_head, count: 1 },
      { level: project_approval_level.finance_L2, count: 1 },
    ],
  },
  [project_approval_level.bu_dept_finance_L3]: {
    threshold: { min: null, max: null },
    approvals_required: [
      { level: project_approval_level.bu_head, count: 1 },
      { level: project_approval_level.dept_head, count: 1 },
      { level: project_approval_level.finance_L3, count: 1 },
    ],
  },
};

